import React from 'react';
import { useSynapse } from 'app/lib/synaptik';
import { styled, Button, Box, Flex, Checkbox, Modal, Text } from '@goodrx/matisse';
import PropTypes from 'prop-types';
import { useToggledState } from 'app/hooks';
import { PaymentMethodProps } from 'app/components/payment/types';

const Cell = styled(Flex)`
  border-radius: 4px;
  cursor: pointer;
  transition: 250ms;
  width: 100%;
`;

const CardImage = styled.img`
  height: 36px;
  border: 1px solid ${(p: any) => p.theme.colors.grey};
  border-radius: 8px;
`;

const CheckboxWrapper = styled(Box)`
  label {
    min-height: initial;
  }
`;

const cardImageFromPaymentMethod = ({ brand = '' }) => brand && brand.toLowerCase().replace(' ', '_');

export function PaymentMethod({ card, isCheckout, isSelected, onSelect }: PaymentMethodProps) {
  const [showDeleteModal, setShowDeleteModal] = useToggledState(false);
  const [deletingPaymentMethod, deletePaymentMethod] = useSynapse(({ user }) => [
    user.state.deletingPaymentMethod,
    user.deletePaymentMethod,
  ]);

  return (
    <Cell key={card.token} justifyContent="space-between" onClick={() => onSelect(card.token)}>
      <Flex flex={1} alignItems="center">
        <CardImage
          src={`/images/cards/${cardImageFromPaymentMethod(card)}.svg`}
          alt={card.brand || 'Credit Card Icon'}
        />

        <Box ml={3} flex={1}>
          <Flex mb={-1} fontSize={14} alignItems="center">
            ••••&nbsp;
            <strong>{card.last4}</strong>
          </Flex>

          <Text fontSize={14} color="greyDarker">
            Expires {card.expirationDate}
          </Text>
        </Box>

        {isCheckout && (
          <CheckboxWrapper id="wrapper">
            <Checkbox
              id={card.token}
              name={card.token}
              iconOn="check-circle"
              iconOff="radiobox-blank"
              iconSize={25}
              value={isSelected}
              colorOff="greyDark"
              isRadio
            />
          </CheckboxWrapper>
        )}

        {!isCheckout && (
          <Button variant="greyText" mr={-3} onClick={setShowDeleteModal}>
            <Text fontSize={14}>Remove</Text>
          </Button>
        )}
      </Flex>

      <Modal open={showDeleteModal} onClose={setShowDeleteModal} maxWidth={350} title="Confirm Card Removal">
        <Modal.Body>
          Are you sure you want to delete the card ending in <strong>{card.last4}</strong>? This is a permanent action
          and cannot be undone.
        </Modal.Body>

        <Modal.Footer>
          <Button.Group justifyContent="flex-end">
            <Button variant="grey" onClick={setShowDeleteModal}>
              Cancel
            </Button>

            <Button
              loading={deletingPaymentMethod}
              variant="danger"
              onClick={async () => deletePaymentMethod(card.token)}>
              Delete
            </Button>
          </Button.Group>
        </Modal.Footer>
      </Modal>
    </Cell>
  );
}

PaymentMethod.propTypes = {
  card: PropTypes.shape({
    brand: PropTypes.string.isRequired,
    last4: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    expirationDate: PropTypes.string.isRequired,
  }).isRequired,
  isCheckout: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
};

PaymentMethod.defaultProps = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSelect() {},
};
